import createContext from "@/helpers/createContext";
import React, { FC, useCallback, useState, useEffect } from "react";
import { User } from "@/types/user";
import { useNavigate } from 'react-router-dom';
import { removeFromLocalStorage } from "@/helpers";
import { logout } from "@/services/api";

interface UserContext {
  user: User;
  setUser: (newUser: User) => void;
  handleLogout: () => Promise<void>;
}

interface UserProviderProps {
  children: React.ReactNode;
  initialValue?: User;
}

export const [useUserContext, UserCtxProvider] = createContext<UserContext>();

const UserProvider: FC<UserProviderProps> = ({ children, initialValue }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User>(() => {
    if (initialValue) return initialValue;

    const persistedUser = localStorage.getItem("user");

    if (!persistedUser) return { accountName: "" };

    return { ...JSON.parse(persistedUser) };
  });

  const updateUser = useCallback((newUser: User) => {
    setUser(newUser);
    localStorage.setItem("user", JSON.stringify(newUser));
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error during logout:', error);
    }
    removeFromLocalStorage("user");
    setUser({ accountName: "" });
    navigate('/auth');
  }, [navigate]);

  useEffect(() => {
    const handleUnauthorized = () => {
      handleLogout();
    };

    window.addEventListener('UNAUTHORIZED', handleUnauthorized);

    return () => {
      window.removeEventListener('UNAUTHORIZED', handleUnauthorized);
    };
  }, [handleLogout]);

  return (
    <UserCtxProvider value={{ user, setUser: updateUser, handleLogout }}>
      {children}
    </UserCtxProvider>
  );
};

export default UserProvider;